import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Plus, Trash2, Archive, History, Settings2, Truck, Loader2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { safeFetchJson } from '@/utils/safeFetch';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const todayIso = () => new Date().toISOString().slice(0, 10);

// Status select options for the "Picked" column.
const PICKED_OPTIONS = [
  { value: '',              label: '—',              fill: '' },
  { value: 'niet_begonnen', label: 'Niet begonnen',  fill: 'bg-rose-100  text-rose-800' },
  { value: 'bezig',         label: 'Bezig',          fill: 'bg-amber-100 text-amber-800' },
  { value: 'beeindigd',     label: 'Beëindigd',      fill: 'bg-emerald-100 text-emerald-800' },
];

const fetcher = (path, opts = {}) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
  return safeFetchJson(`${API}/api${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
};

// ---- Cell renderers ------------------------------------------------------
// All editable cells are uncontrolled (`defaultValue`) so the user can type
// freely without re-rendering the parent on every keystroke. We commit on
// blur — that's the moment the value matters. This single optimisation
// keeps the grid responsive even with 50+ rows on a low-end laptop.

const TextCell = ({ value, onCommit, list, placeholder, className = '', testid }) => {
  const ref = useRef(null);
  // Reset the input when the underlying value changes from upstream
  // (e.g. archive switch). Skipped when the input has focus so the
  // user's typing isn't blown away.
  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current) {
      ref.current.value = value || '';
    }
  }, [value]);
  return (
    <input
      ref={ref}
      defaultValue={value || ''}
      placeholder={placeholder || ''}
      list={list}
      data-testid={testid}
      onBlur={(e) => {
        const v = e.target.value;
        if (v !== (value || '')) onCommit(v);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      className={`w-full px-2 py-1 text-xs bg-transparent border-0 focus:bg-white focus:ring-1 focus:ring-teal-400 rounded ${className}`}
    />
  );
};

const TimeCell = ({ value, onCommit, testid }) => (
  <TextCell value={value} onCommit={onCommit} placeholder="HH:MM" testid={testid} className="text-center font-mono" />
);

const NumberCell = ({ value, onCommit, testid }) => (
  <TextCell
    value={value == null ? '' : String(value)}
    onCommit={(v) => {
      const n = v === '' ? null : Number(v);
      onCommit(Number.isFinite(n) ? n : null);
    }}
    placeholder="0"
    testid={testid}
    className="text-right tabular-nums"
  />
);

const CheckCell = ({ value, onCommit, testid }) => (
  <div className="flex items-center justify-center">
    <Checkbox checked={!!value} onCheckedChange={(v) => onCommit(!!v)} data-testid={testid} />
  </div>
);

const PickedCell = ({ value, onCommit, testid }) => {
  const opt = PICKED_OPTIONS.find(o => o.value === (value || '')) || PICKED_OPTIONS[0];
  return (
    <select
      value={value || ''}
      onChange={(e) => onCommit(e.target.value)}
      data-testid={testid}
      className={`w-full px-2 py-1 text-xs rounded border-0 focus:ring-1 focus:ring-teal-400 ${opt.fill}`}
    >
      {PICKED_OPTIONS.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
};

// Visual tint for the row — green when arrival + departure are
// confirmed AND status is finished, amber when the TE is being worked
// on, default otherwise. Mirrors the spreadsheet's heat-coding.
const rowTint = (r) => {
  if (r.arrival && r.departure && r.picked === 'beeindigd') return 'bg-emerald-50';
  if (r.picked === 'beeindigd') return 'bg-emerald-50/40';
  if (r.picked === 'bezig') return 'bg-amber-50/50';
  if (r.picked === 'niet_begonnen') return 'bg-rose-50/40';
  return '';
};

// ==========================================================================
// TE options dialog
// ==========================================================================
const TeOptionsDialog = ({ open, onOpenChange, options, reload }) => {
  const [newValue, setNewValue] = useState('');
  const add = async () => {
    const v = newValue.trim();
    if (!v) return;
    try {
      await fetcher('/dockplanning/te-options', { method: 'POST', body: JSON.stringify({ value: v }) });
      setNewValue('');
      await reload();
      toast.success('Option added');
    } catch (e) {
      toast.error(e.message || 'Failed to add');
    }
  };
  const remove = async (value) => {
    if (!window.confirm(`Remove "${value}" from the TE dropdown?`)) return;
    try {
      await fetcher(`/dockplanning/te-options/${encodeURIComponent(value)}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      toast.error(e.message || 'Failed to remove');
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="te-options-dialog">
        <DialogHeader>
          <DialogTitle>TE dropdown options</DialogTitle>
          <DialogDescription>
            Values offered as suggestions in the TE column. Users can still type any value manually.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mb-3">
          <Input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="e.g. 260512_RETOUR_A2"
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            data-testid="te-option-new"
          />
          <Button onClick={add} data-testid="te-option-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto space-y-1">
          {options.length === 0 && <p className="text-xs text-muted-foreground">No options yet.</p>}
          {options.map(o => (
            <div key={o.value} className="flex items-center justify-between border rounded px-3 py-1.5 text-sm">
              <span className="font-mono">{o.value}</span>
              <button onClick={() => remove(o.value)} className="text-muted-foreground hover:text-rose-500" data-testid={`te-option-remove-${o.value}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==========================================================================
// Main page
// ==========================================================================
const DockplanningPage = () => {
  const { isGuest } = useAuth();
  const canEdit = !isGuest;
  const [date, setDate] = useState(todayIso());
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [archives, setArchives] = useState([]);
  const [openArchive, setOpenArchive] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetcher(`/dockplanning/rows?date=${date}`);
      setRows(d.rows || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load rows');
    } finally {
      setLoading(false);
    }
  }, [date]);

  const loadOptions = useCallback(async () => {
    try {
      const d = await fetcher('/dockplanning/te-options');
      setOptions(d.options || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadOptions(); }, [loadOptions]);

  // Single optimistic-patch helper used by every cell — keeps the UI
  // responsive (no spinner per keystroke) while the server confirms.
  const patch = useCallback(async (row_id, fields) => {
    setRows(prev => prev.map(r => (r.row_id === row_id ? { ...r, ...fields } : r)));
    try {
      await fetcher(`/dockplanning/rows/${row_id}`, { method: 'PUT', body: JSON.stringify(fields) });
    } catch (e) {
      toast.error(e.message || 'Save failed');
      loadRows();  // pull canonical state on error
    }
  }, [loadRows]);

  const addRow = async () => {
    try {
      const created = await fetcher('/dockplanning/rows', { method: 'POST', body: JSON.stringify({ date }) });
      setRows(prev => [...prev, created]);
    } catch (e) {
      toast.error(e.message || 'Failed to add row');
    }
  };

  const removeRow = async (row_id) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      await fetcher(`/dockplanning/rows/${row_id}`, { method: 'DELETE' });
      setRows(prev => prev.filter(r => r.row_id !== row_id));
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    }
  };

  const archiveAndClear = async () => {
    setArchivingId('pending');
    try {
      const r = await fetcher('/dockplanning/archive', { method: 'POST', body: JSON.stringify({ date }) });
      toast.success(`Archived ${r.row_count} row${r.row_count === 1 ? '' : 's'} for ${date}`);
      setRows([]);
    } catch (e) {
      toast.error(e.message || 'Archive failed');
    } finally {
      setArchivingId(null);
    }
  };

  const loadArchives = useCallback(async () => {
    try {
      const d = await fetcher('/dockplanning/archive');
      setArchives(d.archives || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load archives');
    }
  }, []);

  const openArchiveDetail = async (archive_id) => {
    try {
      const d = await fetcher(`/dockplanning/archive/${archive_id}`);
      setOpenArchive(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load archive');
    }
  };

  const optionValues = useMemo(() => options.map(o => o.value), [options]);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="dockplanning-page">
        {/* Header */}
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Truck className="h-7 w-7 text-teal-600" />
              Dockplanning Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">
              Live grid of outbound TE bookings per dock. Edit any cell directly — changes auto-save.
            </p>
          </div>
          <div className="ml-auto flex flex-wrap items-end gap-3">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-44"
                data-testid="dockplanning-date"
              />
            </div>
            <Button variant="outline" onClick={() => { setOptionsOpen(true); }} data-testid="dockplanning-manage-te">
              <Settings2 className="h-4 w-4 mr-1.5" /> Manage TE list
            </Button>
            <Button variant="outline" onClick={() => { loadArchives(); setHistoryOpen(true); }} data-testid="dockplanning-history">
              <History className="h-4 w-4 mr-1.5" /> History
            </Button>
            {canEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={rows.length === 0 || archivingId === 'pending'}
                    data-testid="dockplanning-archive-btn"
                  >
                    <Archive className="h-4 w-4 mr-1.5" />
                    {archivingId === 'pending' ? 'Archiving…' : 'Archive and clear'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive {date}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      All {rows.length} row{rows.length === 1 ? '' : 's'} will be moved to the read-only
                      archive and the active grid will be cleared. Tomorrow starts blank.
                      This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={archiveAndClear} data-testid="dockplanning-archive-confirm">
                      Archive and clear
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Grid */}
        <Card className="overflow-hidden" data-testid="dockplanning-grid-card">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Native <datalist> drives the dropdown for the TE column.
                  Browsers render it as a free-text input with autocomplete
                  suggestions — exactly the "dropdown but manual input
                  also allowed" behaviour the user asked for. */}
              <datalist id="dockplanning-te-options">
                {optionValues.map(v => <option key={v} value={v} />)}
              </datalist>

              <table className="w-full text-sm border-collapse">
                <thead className="bg-teal-700 text-white text-xs">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium w-40">TE</th>
                    <th className="px-2 py-2 text-left font-medium w-32">Ordernummer</th>
                    <th className="px-2 py-2 font-medium w-16">TE created</th>
                    <th className="px-2 py-2 font-medium w-24">Schema</th>
                    <th className="px-2 py-2 font-medium w-32">Picked</th>
                    <th className="px-2 py-2 font-medium w-20">Dock nr</th>
                    <th className="px-2 py-2 font-medium w-16">Made list</th>
                    <th className="px-2 py-2 font-medium w-16">Cmr printed</th>
                    <th className="px-2 py-2 font-medium w-16">Arrival</th>
                    <th className="px-2 py-2 font-medium w-24">Arrival time</th>
                    <th className="px-2 py-2 font-medium w-16">Departure</th>
                    <th className="px-2 py-2 font-medium w-24">Departure time</th>
                    <th className="px-2 py-2 font-medium w-16">Pallets</th>
                    <th className="px-2 py-2 text-left font-medium min-w-[180px]">Comments</th>
                    <th className="px-2 py-2 font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.row_id} className={`border-t ${rowTint(r)}`} data-testid={`dp-row-${r.row_id}`}>
                      <td className="border px-1">
                        <TextCell
                          value={r.te}
                          onCommit={(v) => patch(r.row_id, { te: v })}
                          list="dockplanning-te-options"
                          placeholder="TE…"
                          testid={`dp-te-${r.row_id}`}
                        />
                      </td>
                      <td className="border px-1"><TextCell value={r.ordernummer} onCommit={(v) => patch(r.row_id, { ordernummer: v })} placeholder="…" testid={`dp-orn-${r.row_id}`} /></td>
                      <td className="border"><CheckCell value={r.te_created} onCommit={(v) => patch(r.row_id, { te_created: v })} testid={`dp-tec-${r.row_id}`} /></td>
                      <td className="border px-1"><TimeCell value={r.schema_time} onCommit={(v) => patch(r.row_id, { schema_time: v })} testid={`dp-sch-${r.row_id}`} /></td>
                      <td className="border px-1"><PickedCell value={r.picked} onCommit={(v) => patch(r.row_id, { picked: v })} testid={`dp-pck-${r.row_id}`} /></td>
                      <td className="border px-1"><TextCell value={r.dock_nr} onCommit={(v) => patch(r.row_id, { dock_nr: v })} placeholder="…" className="text-center" testid={`dp-dock-${r.row_id}`} /></td>
                      <td className="border"><CheckCell value={r.made_list} onCommit={(v) => patch(r.row_id, { made_list: v })} testid={`dp-ml-${r.row_id}`} /></td>
                      <td className="border"><CheckCell value={r.cmr_printed} onCommit={(v) => patch(r.row_id, { cmr_printed: v })} testid={`dp-cmr-${r.row_id}`} /></td>
                      <td className="border"><CheckCell value={r.arrival} onCommit={(v) => patch(r.row_id, { arrival: v })} testid={`dp-arr-${r.row_id}`} /></td>
                      <td className="border px-1"><TimeCell value={r.arrival_time} onCommit={(v) => patch(r.row_id, { arrival_time: v })} testid={`dp-arrt-${r.row_id}`} /></td>
                      <td className="border"><CheckCell value={r.departure} onCommit={(v) => patch(r.row_id, { departure: v })} testid={`dp-dep-${r.row_id}`} /></td>
                      <td className="border px-1"><TimeCell value={r.departure_time} onCommit={(v) => patch(r.row_id, { departure_time: v })} testid={`dp-dept-${r.row_id}`} /></td>
                      <td className="border px-1"><NumberCell value={r.pallets} onCommit={(v) => patch(r.row_id, { pallets: v })} testid={`dp-pal-${r.row_id}`} /></td>
                      <td className="border px-1"><TextCell value={r.comments} onCommit={(v) => patch(r.row_id, { comments: v })} placeholder="—" testid={`dp-com-${r.row_id}`} /></td>
                      <td className="border text-center">
                        {canEdit && (
                          <button onClick={() => removeRow(r.row_id)} className="text-muted-foreground hover:text-rose-500" data-testid={`dp-del-${r.row_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr key="empty">
                      <td colSpan={15} className="text-center py-10 text-sm text-muted-foreground">
                        No rows for {date}. Click "Add row" below to start.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {canEdit && (
                <div className="border-t bg-muted/20 p-2">
                  <Button variant="ghost" onClick={addRow} data-testid="dockplanning-add-row">
                    <Plus className="h-4 w-4 mr-1.5" /> Add row
                  </Button>
                  <span className="ml-3 text-xs text-muted-foreground">
                    Tip: press <kbd className="px-1 rounded bg-muted text-[10px]">Tab</kbd> to move across cells; values save on blur.
                  </span>
                  <Badge variant="outline" className="ml-3 text-[10px]">{rows.length} row{rows.length === 1 ? '' : 's'}</Badge>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* TE options dialog */}
        <TeOptionsDialog open={optionsOpen} onOpenChange={setOptionsOpen} options={options} reload={loadOptions} />

        {/* History list dialog */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-2xl" data-testid="dockplanning-history-dialog">
            <DialogHeader>
              <DialogTitle>Archived days</DialogTitle>
              <DialogDescription>Snapshots are read-only. Click a row to view its full grid.</DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto space-y-1">
              {archives.length === 0 && <p className="text-xs text-muted-foreground">No archives yet.</p>}
              {archives.map(a => (
                <button
                  key={a.archive_id}
                  onClick={() => openArchiveDetail(a.archive_id)}
                  className="w-full flex items-center justify-between border rounded px-3 py-2 text-sm hover:bg-muted/30"
                  data-testid={`dp-archive-${a.archive_id}`}
                >
                  <span>
                    <span className="font-semibold">{a.date}</span>
                    <span className="ml-3 text-xs text-muted-foreground">
                      archived {new Date(a.archived_at).toLocaleString()} · {a.archived_by_name}
                    </span>
                  </span>
                  <Badge variant="outline">{a.row_count} rows</Badge>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Single-archive read-only view */}
        <Dialog open={!!openArchive} onOpenChange={(o) => !o && setOpenArchive(null)}>
          <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Archive — {openArchive?.date}</DialogTitle>
              <DialogDescription>
                {openArchive && (
                  <>archived {new Date(openArchive.archived_at).toLocaleString()} by {openArchive.archived_by_name} · {openArchive.row_count} rows · read-only</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-auto">
              {openArchive && (
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-teal-700 text-white sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">TE</th>
                      <th className="px-2 py-1 text-left">Ordernr</th>
                      <th className="px-2 py-1">Schema</th>
                      <th className="px-2 py-1">Picked</th>
                      <th className="px-2 py-1">Dock</th>
                      <th className="px-2 py-1">Arr.</th>
                      <th className="px-2 py-1">Arr. time</th>
                      <th className="px-2 py-1">Dep.</th>
                      <th className="px-2 py-1">Dep. time</th>
                      <th className="px-2 py-1">Pal</th>
                      <th className="px-2 py-1 text-left">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(openArchive.rows || []).map(r => (
                      <tr key={r.row_id} className={`border-t ${rowTint(r)}`}>
                        <td className="border px-2 py-1 font-mono">{r.te}</td>
                        <td className="border px-2 py-1">{r.ordernummer}</td>
                        <td className="border px-2 py-1 text-center font-mono">{r.schema_time}</td>
                        <td className="border px-2 py-1 text-center">{PICKED_OPTIONS.find(o => o.value === r.picked)?.label || '—'}</td>
                        <td className="border px-2 py-1 text-center">{r.dock_nr}</td>
                        <td className="border px-2 py-1 text-center">{r.arrival ? '✓' : ''}</td>
                        <td className="border px-2 py-1 text-center font-mono">{r.arrival_time}</td>
                        <td className="border px-2 py-1 text-center">{r.departure ? '✓' : ''}</td>
                        <td className="border px-2 py-1 text-center font-mono">{r.departure_time}</td>
                        <td className="border px-2 py-1 text-right tabular-nums">{r.pallets ?? ''}</td>
                        <td className="border px-2 py-1">{r.comments}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenArchive(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default DockplanningPage;
